import { Navigate, useParams } from 'react-router-dom';

import { STOREFRONT_PATHS } from '@rheora/enrollment-contract';

import { useTranslation } from '@fluxora/i18n';
import { Spinner } from '@fluxora/ui';

import { useOfferHandoff } from '../hooks/useOfferHandoff';

/**
 * `/offer/:token` (E-SF16) — the customer's landing from an emailed offer.
 *
 * A THIN HAND-OFF, not a second shelf. The plans the agent offered are rendered
 * by the storefront's own shelf, on the storefront's own pages, in the
 * storefront's own order — because they ARE the storefront's pages. All this
 * route does is get the browser to `GET /bff/offer/:token`, whose 302 mints the
 * seeded funnel session and lands the customer on `/shop/plans`.
 *
 * ── WHY IT RENDERS ANYTHING AT ALL ─────────────────────────────────────────
 * The hand-off is a network round-trip to a route that makes two credentialed
 * upstream calls, so there is a real, visible gap — and an emailed link that
 * flashes white is a link that looks broken. The spinner is the honest report of
 * "we are opening this for you", and it is `role="status"` so a screen-reader user
 * is told the same thing rather than being left on a silent page.
 *
 * ── THE FIVE ASYNC STATES ──────────────────────────────────────────────────
 *   idle     — not reachable: the hand-off starts on mount, with no user action.
 *   loading  — the spinner below, from mount until the browser leaves.
 *   error    — a plainly-unusable token, redirected to the ONE failure page.
 *              Everything the server refuses arrives as the BFF's own 302 to the
 *              same page, so both paths land in the same place.
 *   empty    — not applicable: a token resolves to exactly one offer or to nothing.
 *   success  — never rendered HERE. Success is leaving this route for the shelf.
 *
 * ⚠️ RENDER-ONLY. The decision (is this token usable, and what URL do we go to)
 * is `services/offer-entry.service.ts`; the effect is `hooks/useOfferHandoff.ts`.
 * This file chooses no words and encodes no rule.
 */
export function OfferEntryPage(): JSX.Element {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const action = useOfferHandoff(token);

  if (action.kind === 'unusable') {
    return <Navigate to={STOREFRONT_PATHS.offerUnavailable} replace />;
  }

  return (
    <main className="mx-auto flex max-w-2xl flex-col items-center gap-5 px-4 py-16">
      <h1 className="text-xl font-bold text-primary">{t('shop.offer.entry.heading')}</h1>
      <p className="text-sm text-foreground/70">{t('shop.offer.entry.body')}</p>
      {/*
        `Spinner` already renders `role="status"` with an `aria-label` AND an
        `sr-only` copy of the label, so a screen-reader user hears "opening your
        offer…" rather than silence. A second live region here would announce the
        same sentence twice — one status per page state.
      */}
      <div className="flex justify-center py-4">
        <Spinner size="md" label={t('shop.offer.entry.loading')} />
      </div>
    </main>
  );
}
